import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { Event, EventFilterParams } from '../../types';
import dayjs from 'dayjs';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import apiService from '../../services/api';
import { toast } from 'react-toastify';
import { RequiredLabel } from '../common/FormTextField';

interface EventEditDialogProps {
  open: boolean;
  event: Event | null;
  onClose: () => void;
  onSave: () => void;
}

export interface FormDataType {
  eventName: string;
  // eventCode?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  venue?: string;
  isActive: boolean;
  startTime: string | dayjs.Dayjs | null;
  endTime: string | dayjs.Dayjs | null;
  guestIds: string[];
}

const EventEditDialog: React.FC<EventEditDialogProps> = ({
  open,
  event,
  onClose,
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [guestsOptions, setGuestsOptions] = useState<
    { id: string; name: string }[]
  >([]);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  const [hasMore, setHasMore] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    getValues,
  } = useForm<FormDataType>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      eventName: '',
      // eventCode: '',
      description: '',
      startDate: '',
      endDate: '',
      venue: '',
      isActive: true,
      startTime: null,
      endTime: null,
      guestIds: [],
    },
  });

  const startDate = watch('startDate');

  // ----- Guests state -----
  const loadGuests = async (pageNumber: number) => {
    try {
      const res = await apiService.getGuests({ page: pageNumber, limit: 5 });

      const { data: list = [], totalPages = 1 } = res;

      // stop if page exceeds backend limit
      if (pageNumber > totalPages) {
        setHasMore(false);
        return;
      }

      const guests = list.map((s: any) => {
        const first = s.firstName || s.first_name || s.givenName || '';
        const last = s.lastName || s.last_name || s.familyName || '';

        return {
          id: s.id,
          name: `${first} ${last}`.trim() || s.name || `Speaker ${s.id}`,
        };
      });

      // page 1 → replace (avoid duplicates)
      if (pageNumber === 1) {
        setGuestsOptions(guests);
      } else {
        setGuestsOptions(prev => [...prev, ...guests]);
      }

      // update hasMore
      setHasMore(pageNumber < totalPages);
    } catch (err) {
      console.error('Failed to load guests', err);
    }
  };

  // initial load
  useEffect(() => {
    setGuestsOptions([]);
    setPage(1);
    loadGuests(1);
  }, []);

  // populate form when event changes (edit mode)
  useEffect(() => {
    if (open && event) {
      reset({
        eventName: event.eventName,
        // eventCode: event.eventCode || '',
        description: event.description || '',
        startDate: event.startDate
          ? new Date(event.startDate).toISOString().split('T')[0]
          : '',
        endDate: event.endDate
          ? new Date(event.endDate).toISOString().split('T')[0]
          : '',
        venue:
          typeof event.venue === 'string'
            ? event.venue
            : event.venue?.venueName || '',
        isActive: event.isActive,
        startTime: event.startTime ? dayjs(event.startTime, 'HH:mm') : null,
        endTime: event.endTime ? dayjs(event.endTime, 'HH:mm') : null,
        guestIds: Array.isArray(event.eventGuests)
          ? event.eventGuests.map((g: any) => g.id)
          : [],
      });
    } else if (open && !event) {
      // create mode
      reset({
        eventName: '',
        // eventCode: '',
        description: '',
        startDate: '',
        endDate: '',
        venue: '',
        isActive: true,
        startTime: null,
        endTime: null,
        guestIds: [],
      });
    }
  }, [event, open]);

  ////////////////////// Submit (all data as FormData)///////////////////////////
  const onSubmit = async (data: FormDataType) => {
    setError('');

    setLoading(true);

    try {
      const formattedData = {
        eventName: data.eventName.trim(),
        // eventCode: data.eventCode,
        description: data.description?.trim(),
        startDate: data.startDate,
        endDate: data.endDate,
        startTime: data.startTime
          ? dayjs(data.startTime).format('HH:mm:ss')
          : null,
        endTime: data.endTime ? dayjs(data.endTime).format('HH:mm:ss') : null,
        venue: data.venue?.trim(),
        isActive: true,
        guestIds: data.guestIds,
      };

      if (event) {
        await apiService.updateEvent(event.id, formattedData);
        toast.success('Agenda updated successfully');
      } else {
        await apiService.createEvent(formattedData);
        toast.success('Agenda created successfully');
      }

      reset();
      onSave();
      onClose();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${event ? 'update' : 'create'} event`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError('');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
            {event ? 'Edit Agenda' : 'Create New Agenda'}
          </Typography>
          {event && (
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              {event.eventName}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent
          sx={{
            maxHeight: { xs: '70vh', sm: '75vh', md: '80vh' },
            overflowY: 'auto',
            p: { xs: 2, sm: 3 },
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* event name */}
            <Grid item xs={12}>
              <TextField
                {...register('eventName', {
                  required: 'Agenda name is required',
                  minLength: {
                    value: 3,
                    message: 'Agenda name must be at least 3 characters',
                  },
                })}
                label={<RequiredLabel label="Agenda Name" />}
                fullWidth
                error={!!errors.eventName}
                helperText={errors.eventName?.message}
                disabled={loading}
              />
            </Grid>

            {/* event code */}
            {/* <Grid item xs={12} sm={6}>
              <TextField
                {...register('eventCode', {
                  required: 'Event Code is required',
                })}
                label="Event Code"
                fullWidth
                error={!!errors.eventCode}
                helperText={errors.eventCode?.message}
                disabled={loading}
              />
            </Grid> */}

            {/* active toogle */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={e => field.onChange(e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label={field.value ? 'Active Agenda' : 'Inactive Agenda'}
                  />
                )}
              />
            </Grid>

            {/* description */}
            <Grid item xs={12}>
              <TextField
                {...register('description', {
                  required: 'Agenda description is required',
                })}
                label={<RequiredLabel label="Description" />}
                fullWidth
                multiline
                rows={4}
                disabled={loading}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            </Grid>

            {/* venue */}
            <Grid item xs={12}>
              <TextField
                {...register('venue', { required: 'Agenda venue is required' })}
                label={<RequiredLabel label="Venue" />}
                fullWidth
                disabled={loading}
                error={!!errors.venue}
                helperText={errors.venue?.message}
              />
            </Grid>

            {/* start date */}
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('startDate', {
                  required: 'Start Date is required',
                })}
                label={<RequiredLabel label="Start Date" />}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.startDate}
                helperText={errors.startDate?.message}
                disabled={loading}
              />
            </Grid>

            {/* end date */}
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('endDate', {
                  required: 'End Date is required',
                  validate: value => {
                    const s = startDate;
                    if (value && s && new Date(value) < new Date(s))
                      return 'End date must be after start date';
                    return true;
                  },
                })}
                label={<RequiredLabel label="End Date" />}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.endDate}
                helperText={errors.endDate?.message}
                disabled={loading}
                inputProps={{ min: startDate || undefined }}
              />
            </Grid>

            {/* start time */}
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Controller
                  name="startTime"
                  control={control}
                  rules={{ required: 'Start time required' }}
                  render={({ field }) => (
                    <TimePicker
                      label={<RequiredLabel label="Start Time" />}
                      {...field}
                      ampm
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!errors.startTime}
                          helperText={errors.startTime?.message}
                        />
                      )}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            {/* end time */}
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Controller
                  name="endTime"
                  control={control}
                  rules={{
                    required: 'End time required',
                    validate: value => {
                      if (!value) return 'End time required';
                      const st = dayjs(getValues('startTime'));
                      const et = dayjs(value);
                      return (
                        et.isAfter(st) || 'End time must be after start time'
                      );
                    },
                  }}
                  render={({ field }) => (
                    <TimePicker
                      label={<RequiredLabel label="End Time" />}
                      {...field}
                      ampm
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!errors.endTime}
                          helperText={errors.endTime?.message}
                        />
                      )}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            {/* autocomplete Guests */}
            <Grid item xs={12}>
              <Controller
                name="guestIds"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    multiple
                    options={guestsOptions}
                    value={guestsOptions.filter(x =>
                      field.value.includes(x.id)
                    )}
                    onChange={(_, selected) =>
                      field.onChange(selected.map(s => s.id))
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    getOptionLabel={option => option.name}
                    ListboxProps={{
                      onScroll: async (e: any) => {
                        const list = e.currentTarget;
                        const reachedBottom =
                          list.scrollTop + list.clientHeight >=
                          list.scrollHeight - 40;

                        if (reachedBottom && !loadingMore && hasMore) {
                          setLoadingMore(true);

                          const next = page + 1;
                          await loadGuests(next);
                          setPage(next);

                          setLoadingMore(false);
                        }
                      },
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Guests"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                    renderGroup={params => (
                      <li key={params.key}>
                        <ul>{params.children}</ul>
                        {loadingMore && (
                          <Box sx={{ textAlign: 'center', py: 1 }}>
                            <CircularProgress size={20} />
                          </Box>
                        )}
                      </li>
                    )}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            variant="outlined"
            color="inherit"
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              color: '#d32f2f',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.15)',
              },
            }}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {loading
              ? event
                ? 'Updating...'
                : 'Creating...'
              : event
                ? 'Update Agenda'
                : 'Create Agenda'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EventEditDialog;
